import { useEffect, useState, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { useTemplate } from '@/contexts/TemplateContext'
import type { GalleryLightboxProps } from './types'

export function GalleryLightbox({ images, initialIndex = 0, onClose }: GalleryLightboxProps) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex)
  const [isZoomed, setIsZoomed] = useState(false)
  const { colors } = useTemplate()

  const navigate = useCallback((direction: 'prev' | 'next') => {
    setIsZoomed(false)
    if (direction === 'prev' && currentIndex > 0) {
      setCurrentIndex((i) => i - 1)
    } else if (direction === 'next' && currentIndex < images.length - 1) {
      setCurrentIndex((i) => i + 1)
    }
  }, [currentIndex, images.length])

  // Keyboard handler
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') navigate('prev')
      else if (e.key === 'ArrowRight') navigate('next')
      else if (e.key === 'Escape') onClose()
      else if (e.key === ' ' || e.key === 'Enter') setIsZoomed((z) => !z)
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [navigate, onClose])

  // Prevent body scroll
  useEffect(() => {
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = ''
    }
  }, [])

  const currentImage = images[currentIndex]

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Image lightbox"
      className="fixed inset-0 z-[9999] flex items-center justify-center"
      style={{ backgroundColor: 'rgba(0,0,0,0.95)' }}
      onClick={onClose}
    >
      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-20 p-2 rounded-full opacity-80 hover:opacity-100 transition-opacity"
        style={{ backgroundColor: colors.colors.primary, color: colors.colors.secondary }}
        aria-label="Close lightbox"
      >
        ✕
      </button>

      {/* Counter */}
      <span className="absolute top-4 left-4 text-white text-sm z-20">
        {currentIndex + 1} / {images.length}
      </span>

      {/* Navigation - Prev */}
      {currentIndex > 0 && (
        <button
          onClick={(e) => {
            e.stopPropagation()
            navigate('prev')
          }}
          className="absolute left-4 top-1/2 -translate-y-1/2 z-20 p-3 rounded-full opacity-80 hover:opacity-100 transition-all hover:scale-110"
          style={{ backgroundColor: colors.colors.primary, color: colors.colors.secondary }}
          aria-label="Previous image"
        >
          ←
        </button>
      )}

      {/* Navigation - Next */}
      {currentIndex < images.length - 1 && (
        <button
          onClick={(e) => {
            e.stopPropagation()
            navigate('next')
          }}
          className="absolute right-4 top-1/2 -translate-y-1/2 z-20 p-3 rounded-full opacity-80 hover:opacity-100 transition-all hover:scale-110"
          style={{ backgroundColor: colors.colors.primary, color: colors.colors.secondary }}
          aria-label="Next image"
        >
          →
        </button>
      )}

      {/* Image */}
      <img
        src={currentImage.url}
        alt={currentImage.alt_text || currentImage.caption || 'Gallery image'}
        className="max-w-[90vw] max-h-[90vh] object-contain cursor-zoom-in"
        style={{ transform: isZoomed ? 'scale(1.5)' : 'scale(1)', transition: 'transform 0.3s ease' }}
        onClick={(e) => {
          e.stopPropagation()
          setIsZoomed((z) => !z)
        }}
      />

      {/* Caption */}
      {currentImage.caption && (
        <div className="absolute bottom-4 left-0 right-0 text-center z-20">
          <p className="text-white text-sm">{currentImage.caption}</p>
        </div>
      )}
    </div>,
    document.body
  )
}
